import { Button } from '@progress/kendo-react-buttons'
import classNames from 'classnames'
import React from 'react'
import * as R from 'remeda'

import { Box } from '~/components/elements/Box'
import { Icon } from '~/components/elements/Icon'
import { VStack } from '~/components/elements/VStack'
import { useToggleAnimation } from '~/hooks/useToggleAnimation'
import { mapItemsToObj, type NavigationGroup } from '~/utils'

import styles from './AccordionNavigation.css'

type ChildrenParams<TGroupKey extends string> = {
  groupMap: Record<TGroupKey, NavigationGroup<TGroupKey>>
}

export type AccordionNavigationProps<TGroupKey extends string> = {
  children?: (params: ChildrenParams<TGroupKey>) => React.ReactNode
  'data-testid'?: string
  groups: NavigationGroup<TGroupKey>[]
  isExpanded?: boolean
  onToggle?: () => void
}

export const AccordionNavigation = <TGroupKey extends string>({
  children,
  'data-testid': dataTestId = 'components-features-accordion-navigation',
  groups,
  isExpanded = false,
  onToggle,
}: AccordionNavigationProps<TGroupKey>) => {
  const [animationClassName, handleAnimationEnd] = useToggleAnimation(
    isExpanded,
    [styles.collapsed, styles.expand, styles.expanded, styles.collapse],
  )

  const availableGroups = React.useMemo(
    () => groups.filter((group) => 0 < group.items.length),
    [groups],
  )

  return (
    <Box
      className={classNames(styles.root, animationClassName)}
      data-testid={dataTestId}
      onAnimationEnd={handleAnimationEnd}
    >
      <VStack className={styles.collapsedNavigation} spacing={0}>
        {availableGroups.map((group) => (
          <Button
            key={group.groupKey}
            className={classNames(
              styles.collapsedButton,
              group.items.some((item) => item.isActive) &&
                styles.activeCollapsedButton,
            )}
            fillMode="flat"
            onClick={onToggle}
          >
            <Icon icon={group.icon} size="1rem" />
          </Button>
        ))}
      </VStack>

      <VStack className={styles.expandedNavigation} spacing={2}>
        {children?.({
          groupMap: mapItemsToObj(groups, R.identity(), 'groupKey'),
        })}
      </VStack>
    </Box>
  )
}
